import { token } from '@atlaskit/tokens';
import { css } from '@compiled/react';
import type { NextBestAction } from '@post-office/confluence-next-best-action';
import {
	type BackendMessageBody,
	type DefaultMessagesResponse,
	createPlacement,
} from '@post-office/placement-common-v2';
import { placementMiddlewareWithCacheControlNav } from '@post-office/placement-common-v2/middlewares/custom-headers';
import type { SerializableRecord } from '@post-office/serializable-record';
import { usePlacementApi } from '@post-office/use-placement-api';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { usePostOfficeSideNavMessages } from './contexts/PostOfficeSideNavMessageProvider';
import FeatureGates from '@atlaskit/feature-gate-js-client';

const { createFrontend, createBackend } = createPlacement({
	placementId: 'confluence-side-nav',
	options: {
		choreographer: {
			isDefaultEnabled: false,
		},
	},
});

// https://switcheroo.atlassian.com/ui/gates/8ed0ad9f-e13a-4f77-857c-9766c90df936/key/po_enable_confluence-side-nav-implicit-only
const isImplicitOnlyEnabled = FeatureGates.checkGate('po_enable_confluence-side-nav-implicit-only');

export const PlacementBackend = isImplicitOnlyEnabled
	? createBackend({
			customMiddleware: placementMiddlewareWithCacheControlNav,
			params: { dataRetrivalType: 'implicit-only' },
		})
	: createBackend({ customMiddleware: placementMiddlewareWithCacheControlNav });

// Props passed from Product to Placement
type PlacementFrontendProps = {
	preloadedInitialData: DefaultMessagesResponse | undefined;
	onDismiss: (messages: { messages: BackendMessageBody<SerializableRecord>[] }) => void;
	onSideNavClick: (
		nextBestActionType: NextBestAction,
		preloadedMessageState: { messages: BackendMessageBody<SerializableRecord>[] },
	) => void;
	onCallback: (callbackKey: NextBestAction) => Promise<void>;
	currentRoute?: string;
};

const confluenceSideNavStyles = css({
	display: 'flex',
	margin: token('space.150', '12px'),
});

const SORTED_NBA: NextBestAction[] = [
	'CREATE_WITH_ROVO_NUDGE',
	'CREATE_SPACE',
	'CREATE_PAGE',
	'CREATE_WHITEBOARD',
	'1_ON_1_LOOM_NUDGE',
	'LOOM_AI_MEETING_NOTES',
	'RESURRECTED_USER_NUDGE',
];

// todo: Resolve backend ordering incorrectly
const sortMessages = (messages: BackendMessageBody<SerializableRecord>[] = []) => {
	return messages.sort((a, b) => {
		return (
			// @ts-expect-error Property 'nextBestActionType' does not exist on type 'BackendMessageBody<SerializableRecord>'
			SORTED_NBA.indexOf(a?.nextBestActionType as NextBestAction) -
			// @ts-expect-error Property 'nextBestActionType' does not exist on type 'BackendMessageBody<SerializableRecord>'
			SORTED_NBA.indexOf(b?.nextBestActionType as NextBestAction)
		);
	});
};

export const PlacementFrontend = createFrontend(
	(Messages) =>
		({
			preloadedInitialData,
			onDismiss,
			onSideNavClick,
			onCallback,
			currentRoute,
		}: PlacementFrontendProps) => {
			const [{ messages, status }, setMessageState] = useState<{
				messages: BackendMessageBody<SerializableRecord>[] | undefined;
				status: 'loading' | 'done';
			}>(() => {
				if (preloadedInitialData) {
					return preloadedInitialData?.messages?.length > 0
						? {
								status: 'done',
								messages: sortMessages(preloadedInitialData?.messages),
							}
						: { messages: undefined, status: 'done' };
				}

				return { messages: undefined, status: 'loading' };
			});

			const context = usePostOfficeSideNavMessages();

			const isDone = status === 'done';

			const { data, error, isLoading } = usePlacementApi<DefaultMessagesResponse>('/', {
				autoRefetch: {
					isEnabled: !isDone,
					updateKey: 'confluence-side-nav',
				},
			});

			const handleOnDismissed = useCallback(() => {
				// no more messages until the next fetch
				onDismiss({ messages: [] });

				setMessageState({ messages: undefined, status: 'done' });
			}, [onDismiss]);

			const handleOnSideNavClick = useCallback(
				(nextBestActionType: NextBestAction) => {
					// display the next message
					setMessageState((prevState) => {
						if (prevState.messages) {
							const newMessages = prevState.messages.slice(1);

							onSideNavClick(nextBestActionType, { messages: newMessages });

							return {
								messages: newMessages,
								status: 'done',
							};
						}

						onSideNavClick(nextBestActionType, { messages: [] });

						return prevState;
					});
				},
				[onSideNavClick],
			);

			useEffect(() => {
				if (!isLoading && !isDone) {
					setMessageState({
						messages: sortMessages(data?.messages),
						status: 'done',
					});
				}
			}, [isLoading, isDone, data]);

			const displayedMessage = useMemo(() => {
				if (!messages) {
					return;
				}

				const filteredMessages = messages.filter((message) => {
					// @ts-expect-error Property 'explicitEnableRequired' does not exist on type 'BackendMessageBody<SerializableRecord>'
					if (message.explicitEnableRequired as boolean) {
						// @ts-expect-error Property 'nextBestActionType' does not exist on type 'BackendMessageBody<SerializableRecord>'
						return context?.enabledMessages.includes(message.nextBestActionType as NextBestAction);
					}

					if (message.messageTemplateId === 'rovo-extension-install-cta-left-nav') {
						// Don't show on FPS to avoid collision with existing install CTA (built outside of PO)
						if (currentRoute?.includes('/search')) {
							return false;
						}
					}

					return true;
				});

				return filteredMessages[0];
			}, [messages, context, currentRoute]);

			if (error) {
				return null;
			}

			if (status === 'loading') {
				return null;
			}

			return displayedMessage ? (
				<div css={confluenceSideNavStyles}>
					<Messages.render
						{...displayedMessage}
						onSideNavClick={handleOnSideNavClick}
						handleOnDismissed={handleOnDismissed}
						onCallback={onCallback}
					/>
				</div>
			) : null;
		},
);
